import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import BaseBadge from './BaseBadge';

const PROJECTS = [
  {
    title: "ABA Clinical Compliance & Billing Portal",
    points: [
      "HIPAA-compliant secure data vaulting",
      "Direct insurance billing code translation",
      "Conflict-free scheduling for practitioners",
      "Audit-ready clinical logs & tracking",
      "Low-latency multi-terminal sync"
    ],
    thumbnailUrl: "/assets/Villa ABA Workflow Screenshot.PNG",
    showStaticImage: true
  },
  {
    title: "Traveler System for Manufacturers",
    points: [
      "User configurable Quality Assurance gates",
      "Project Tracking",
      "Employee authorization",
      "Manager Sign-offs and flagging",
      "50+ device concurrency"
    ],
    videoUrl: "/assets/traveler-system.mp4",
  },
  {
    title: "Global Supply Chain Dashboard",
    points: [
      "Real-time inventory sync",
      "Predictive mapping",
      "Comprehensive logistics view",
      "Scaling across regions"
    ],
    videoUrl: "/assets/traveler-system.mp4",
  },
  {
    title: "Client Financial Portal",
    points: [
      "Secure document vaulting",
      "Interactive charting",
      "Complex data visualization",
      "Simplified end-user experience"
    ],
    videoUrl: "/assets/traveler-system.mp4",
  }
];

/**
 * FeaturedWorkBadge
 * Displays ONE spotlight project with an auto-playing video 
 * and a CTA routing to the full Portfolio page.
 */
const FeaturedWorkBadge = ({ onSeePortfolio }) => {
  const videoRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [project, setProject] = useState(null);

  // Play video on mount & pick random project
  useEffect(() => {
    // Pick a random project on mount
    const randomIndex = Math.floor(Math.random() * PROJECTS.length);
    const selected = PROJECTS[randomIndex];
    setProject(selected);

    // Give it a tiny delay to allow src assignment before playing
    setTimeout(() => {
      if (videoRef.current && !selected.showStaticImage) {
        videoRef.current.play().catch(err => {
          console.debug("Autoplay prevented on FeaturedWorkBadge:", err);
        });
      }
    }, 50);
  }, []);

  if (!project) return null; // Avoid render errors before effect runs



  return (
    <BaseBadge colorFrom="#a855f7" colorTo="#ec4899" title="Featured Work">
      {/* Main Content Area - Clickable */}
      <div 
        onClick={onSeePortfolio}
        className="flex flex-col flex-grow justify-between cursor-pointer group/card"
      >
        
        {/* Top: Video Spotlight & Info */}
        <div className="space-y-4">
          
          {/* Video Container (16:9 Aspect Ratio) */}
          <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-white/10 bg-black/40 shadow-inner group">
            {project.showStaticImage ? (
              <img
                src={project.thumbnailUrl}
                alt={project.title}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 opacity-100"
              />
            ) : (
              <>
                {/* Loading Skeleton */}
                {!isVideoLoaded && (
                  <div className="absolute inset-0 animate-pulse bg-white/5 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                )}
                
                <video
                  ref={videoRef}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
                  src={project.videoUrl}
                  muted
                  loop
                  playsInline
                  onLoadedData={() => setIsVideoLoaded(true)}
                />
              </>
            )}
            
            {/* Subtle Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
          </div>

          {/* Project Teaser Info */}
          <div className="flex flex-col gap-3">
            <h3 className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500 font-bold text-[18px] leading-tight tracking-wide">
              {project.title}
            </h3>
            
            {/* Bulleted Capabilities List */}
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-2">
              {project.points.slice(0, 6).map((point, idx) => (
                <li key={idx} className="flex items-start gap-2 text-[13.5px] text-white/70 leading-snug">
                  <div className="mt-1 shrink-0 text-purple-400/80">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="line-clamp-2">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom: Subtle CTA */}
        <div className="mt-8">
          <motion.div
            className="w-full relative overflow-hidden group-hover/card:bg-purple-500/30 px-6 py-4 rounded-xl bg-purple-500/10 border border-purple-400/20 font-medium flex justify-center items-center text-white/90 transition-colors"
          >
            <span className="relative z-10 tracking-widest uppercase text-sm">Visit Portfolio</span>
            <svg className="w-4 h-4 ml-2 relative z-10 opacity-70 group-hover/card:translate-x-1 group-hover/card:opacity-100 transition-all" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </motion.div>
        </div>
      </div>

    </BaseBadge>
  );
};

export default FeaturedWorkBadge;
